import { useState, useEffect } from "react";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import {
  Wrench,
  Clock,
  CheckCircle,
  AlertTriangle,
  Package,
  Loader2,
  Send,
  FileText,
  MapPin,
  Phone,
  Mail,
  User,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

// Status Badge Component
const StatusBadge = ({ status }) => {
  const statusStyles = {
    pending: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
    assigned: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    ongoing: "bg-purple-500/10 text-purple-400 border-purple-500/20",
    complete: "bg-green-500/10 text-green-400 border-green-500/20",
    needs_more_work: "bg-orange-500/10 text-orange-400 border-orange-500/20",
    deferred: "bg-slate-500/10 text-slate-400 border-slate-500/20",
    needs_parts: "bg-red-500/10 text-red-400 border-red-500/20"
  };

  const statusLabels = {
    pending: "Pending",
    assigned: "Assigned",
    ongoing: "In Progress",
    complete: "Complete",
    needs_more_work: "Needs More Work",
    deferred: "Deferred",
    needs_parts: "Needs Parts"
  };

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${statusStyles[status] || statusStyles.pending}`}>
      {statusLabels[status] || status?.replace(/_/g, ' ').toUpperCase()}
    </span>
  );
};

// Request Card Component
const RequestCard = ({ request, onUpdate, workers }) => {
  const [expanded, setExpanded] = useState(false);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [updateForm, setUpdateForm] = useState({
    status: request.status || "assigned",
    scope_of_work: "",
    notes: "",
    parts_needed: "",
    deferred_to: ""
  });
  const [loading, setLoading] = useState(false);

  const handleSubmitUpdate = async () => {
    setLoading(true);
    try {
      await onUpdate(request.id, updateForm);
      setShowUpdateDialog(false);
      setUpdateForm({
        status: "assigned",
        scope_of_work: "",
        notes: "",
        parts_needed: "",
        deferred_to: ""
      });
    } finally {
      setLoading(false);
    }
  };

  const priorityColors = {
    emergency: "border-l-red-500 bg-red-500/5",
    high: "border-l-orange-500 bg-orange-500/5",
    normal: "border-l-blue-500",
    low: "border-l-slate-500"
  };

  return (
    <>
      <div className={`card-clinical border-l-4 ${priorityColors[request.data?.priority] || priorityColors.normal} overflow-hidden`}>
        {/* Header */}
        <div 
          className="p-4 cursor-pointer hover:bg-zinc-800/30 transition-colors"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-white font-mono text-sm">#{request.id}</span>
                <StatusBadge status={request.status} />
                {request.data?.priority === "emergency" && (
                  <span className="px-2 py-0.5 bg-red-500/20 text-red-400 rounded text-xs font-medium">
                    EMERGENCY
                  </span>
                )}
              </div>
              <h3 className="text-white font-semibold">{request.request_type_label || request.request_type}</h3>
              <p className="text-slate-400 text-sm">{request.data?.hospital_name}</p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={(e) => { e.stopPropagation(); setShowUpdateDialog(true); }}
                className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border border-blue-500/30"
                data-testid={`update-${request.id}`}
              >
                <FileText className="w-4 h-4 mr-1" />
                Update
              </Button>
              {expanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
            </div>
          </div>
        </div>

        {/* Expanded Details */}
        {expanded && (
          <div className="px-4 pb-4 border-t border-zinc-800 pt-4 space-y-4">
            {/* Contact Info */}
            <div className="bg-zinc-900/50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-400 mb-3">Contact Information</h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2 text-slate-300">
                  <User className="w-4 h-4 text-slate-500" />
                  {request.data?.contact_name}
                </div>
                <div className="flex items-center gap-2 text-slate-300">
                  <Phone className="w-4 h-4 text-slate-500" />
                  {request.data?.contact_phone}
                </div>
                <div className="flex items-center gap-2 text-slate-300 col-span-2">
                  <Mail className="w-4 h-4 text-slate-500" />
                  {request.data?.contact_email}
                </div>
                <div className="flex items-start gap-2 text-slate-300 col-span-2">
                  <MapPin className="w-4 h-4 text-slate-500 mt-0.5" />
                  {request.data?.address}
                </div>
              </div>
            </div>

            {/* Issue Description */}
            <div className="bg-zinc-900/50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-400 mb-2">Issue Description</h4>
              <p className="text-slate-300 text-sm">{request.data?.description}</p>
              
              {/* Device Info if present */}
              {request.data?.device_serial && (
                <div className="mt-3 pt-3 border-t border-zinc-800">
                  <p className="text-xs text-slate-500">Device Serial: <span className="text-slate-300">{request.data.device_serial}</span></p>
                  {request.data?.device_model && (
                    <p className="text-xs text-slate-500">Model: <span className="text-slate-300">{request.data.device_model}</span></p>
                  )}
                  {request.data?.issue_details && (
                    <p className="text-xs text-slate-500 mt-2">Details: <span className="text-slate-300">{request.data.issue_details}</span></p>
                  )}
                </div>
              )}
            </div>

            {/* Work Order History */}
            {request.work_orders && request.work_orders.length > 0 && (
              <div className="bg-zinc-900/50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-emerald-400 mb-3">Work Order History</h4>
                <div className="space-y-3">
                  {request.work_orders.map((wo, idx) => (
                    <div key={wo.id || idx} className="border-l-2 border-zinc-700 pl-3 py-2">
                      <div className="flex items-center justify-between mb-1">
                        <StatusBadge status={wo.status} />
                        <span className="text-xs text-slate-500">
                          {new Date(wo.updated_at).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-xs text-slate-400">By: {wo.updated_by_name}</p>
                      {wo.scope_of_work && (
                        <p className="text-sm text-slate-300 mt-1"><strong>Work Done:</strong> {wo.scope_of_work}</p>
                      )}
                      {wo.notes && (
                        <p className="text-sm text-slate-300 mt-1"><strong>Notes:</strong> {wo.notes}</p>
                      )}
                      {wo.parts_needed && (
                        <p className="text-sm text-amber-400 mt-1"><strong>Parts Needed:</strong> {wo.parts_needed}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Created At */}
            <p className="text-xs text-slate-500">
              Created: {new Date(request.created_at).toLocaleString()}
            </p>
          </div>
        )}
      </div>

      {/* Update Dialog */}
      <Dialog open={showUpdateDialog} onOpenChange={setShowUpdateDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Update Work Order</DialogTitle>
            <DialogDescription className="text-slate-400">
              Request #{request.id} - {request.request_type_label}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="form-group">
              <Label className="text-slate-300">Status</Label>
              <Select
                value={updateForm.status}
                onValueChange={(value) => setUpdateForm({ ...updateForm, status: value })}
              >
                <SelectTrigger className="bg-zinc-950/50 border-zinc-800 text-white" data-testid="status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="ongoing" className="text-slate-200">In Progress</SelectItem>
                  <SelectItem value="complete" className="text-green-400">Complete</SelectItem>
                  <SelectItem value="needs_more_work" className="text-orange-400">Needs More Work</SelectItem>
                  <SelectItem value="needs_parts" className="text-red-400">Needs Additional Parts</SelectItem>
                  <SelectItem value="deferred" className="text-slate-400">Defer to Another Crew Member</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="form-group">
              <Label className="text-slate-300">Scope of Work Performed</Label>
              <Textarea
                placeholder="Describe the work you performed..."
                value={updateForm.scope_of_work}
                onChange={(e) => setUpdateForm({ ...updateForm, scope_of_work: e.target.value })}
                className="input-clinical min-h-[100px]"
                data-testid="scope-of-work"
              />
            </div>

            <div className="form-group">
              <Label className="text-slate-300">Notes</Label>
              <Textarea
                placeholder="Any additional notes..."
                value={updateForm.notes}
                onChange={(e) => setUpdateForm({ ...updateForm, notes: e.target.value })}
                className="input-clinical min-h-[80px]"
                data-testid="work-notes"
              />
            </div>

            {updateForm.status === "needs_parts" && (
              <div className="form-group">
                <Label className="text-amber-400">Parts Needed</Label>
                <Textarea
                  placeholder="List the parts required..."
                  value={updateForm.parts_needed}
                  onChange={(e) => setUpdateForm({ ...updateForm, parts_needed: e.target.value })}
                  className="input-clinical min-h-[80px] border-amber-500/30"
                  data-testid="parts-needed"
                />
              </div>
            )}

            {updateForm.status === "deferred" && (
              <div className="form-group">
                <Label className="text-slate-300">Defer To</Label>
                <Select
                  value={updateForm.deferred_to}
                  onValueChange={(value) => setUpdateForm({ ...updateForm, deferred_to: value })}
                >
                  <SelectTrigger className="bg-zinc-950/50 border-zinc-800 text-white" data-testid="defer-select">
                    <SelectValue placeholder="Select a crew member..." />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {workers.map((worker) => (
                      <SelectItem key={worker.id} value={worker.id} className="text-slate-200">
                        {worker.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUpdateDialog(false)} className="bg-zinc-800 border-zinc-700">
                Cancel
              </Button>
              <Button onClick={handleSubmitUpdate} className="btn-primary" disabled={loading}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Submit Update
                  </>
                )}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Main Worker Dashboard
export default function WorkerDashboard() {
  const { token, user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const fetchData = async () => {
    try {
      const [reqRes, workersRes] = await Promise.all([
        fetch(`${API}/maintenance/requests`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/workers`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ ok: false }))
      ]);
      
      if (reqRes.ok) {
        const data = await reqRes.json();
        setRequests(data.requests || []);
      }
      if (workersRes.ok) {
        const data = await workersRes.json();
        setWorkers(data.workers?.filter(w => w.id !== user.id) || []);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const handleUpdateWorkOrder = async (requestId, updateData) => {
    try {
      const response = await fetch(`${API}/maintenance/requests/${requestId}/work-order`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        toast.success("Work order updated successfully");
        fetchData();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to update work order");
      }
    } catch (error) {
      toast.error("Failed to update work order");
    }
  };

  const filteredRequests = requests.filter(req => {
    if (filter === "all") return true;
    if (filter === "active") return ["assigned", "ongoing", "needs_more_work"].includes(req.status);
    if (filter === "complete") return req.status === "complete";
    if (filter === "pending") return ["needs_parts", "deferred"].includes(req.status);
    return true;
  });

  const stats = {
    total: requests.length,
    active: requests.filter(r => ["assigned", "ongoing", "needs_more_work"].includes(r.status)).length,
    complete: requests.filter(r => r.status === "complete").length,
    needsAttention: requests.filter(r => ["needs_parts", "deferred"].includes(r.status)).length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="p-6 animate-fade-in">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
          My Work Orders
        </h1>
        <p className="text-slate-400 text-sm">Welcome back, {user?.name}</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="card-clinical p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <Wrench className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.total}</p>
              <p className="text-xs text-slate-400">Total Assigned</p>
            </div>
          </div>
        </div>
        <div className="card-clinical p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.active}</p>
              <p className="text-xs text-slate-400">Active</p>
            </div>
          </div>
        </div>
        <div className="card-clinical p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.complete}</p>
              <p className="text-xs text-slate-400">Completed</p>
            </div>
          </div>
        </div>
        <div className="card-clinical p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.needsAttention}</p>
              <p className="text-xs text-slate-400">Needs Attention</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {[
          { value: "all", label: "All" },
          { value: "active", label: "Active" },
          { value: "complete", label: "Completed" },
          { value: "pending", label: "Needs Attention" }
        ].map((tab) => (
          <button
            key={tab.value}
            onClick={() => setFilter(tab.value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              filter === tab.value
                ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                : 'text-slate-400 hover:text-white hover:bg-zinc-800 border border-transparent'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Requests List */}
      <div className="space-y-4">
        {filteredRequests.length === 0 ? (
          <div className="card-clinical p-12 text-center">
            <Wrench className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">No work orders found</p>
          </div>
        ) : (
          filteredRequests.map((request) => (
            <RequestCard
              key={request.id}
              request={request}
              onUpdate={handleUpdateWorkOrder}
              workers={workers}
            />
          ))
        )}
      </div>
    </div>
  );
}
